import React, { useEffect, useState } from 'react'
import './AddPumpModal.scss'
import { ModalWrapper } from 'Components'
import { handleError } from 'Helpers'
import { ToastContainer } from 'react-toastify'
import fillingStationsService, {
  FillingStationPump,
  PumpNozzle,
  PumpProductType,
} from 'BoostXpressAdmin/Services/fillingStationsService'
import PumpNozzleSelector from './PumpNozzleSelector'

interface EditPumpModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  stationId: string | undefined
  pump: FillingStationPump | null
  productTypes: PumpProductType[]
}

interface EditPumpForm {
  fuelFactPumpId: string
  pumpName: string
  productType: PumpProductType
  isActive: boolean
  nozzles: PumpNozzle[]
}

const getFormValues = (
  pump: FillingStationPump | null,
  productTypes: PumpProductType[],
): EditPumpForm => ({
  fuelFactPumpId: pump?.fuelFactPumpSideId ?? '',
  pumpName: pump?.pumpName ?? '',
  productType: pump?.productType ?? productTypes[0] ?? '',
  isActive: pump?.isActive ?? true,
  nozzles: pump?.nozzles ?? [],
})

const EditPumpModal: React.FC<EditPumpModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  stationId,
  pump,
  productTypes,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const availableProductTypes = Array.from(
    new Set([...(pump?.productType ? [pump.productType] : []), ...productTypes]),
  )
  const [values, setValues] = useState<EditPumpForm>(() =>
    getFormValues(pump, availableProductTypes),
  )

  useEffect(() => {
    setValues(getFormValues(pump, availableProductTypes))
  }, [pump, productTypes])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target

    setValues((currentValues) => ({
      ...currentValues,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : name === 'productType'
            ? (value as PumpProductType)
            : value,
    }))
  }

  const handleClose = () => {
    if (isSubmitting) return
    setValues(getFormValues(pump, availableProductTypes))
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (
      !stationId ||
      !pump ||
      !values.fuelFactPumpId.trim() ||
      !values.pumpName.trim() ||
      !values.productType
    ) {
      handleError({ response: { data: { error: 'Please fill in all required fields' } } })
      return
    }

    if (values.nozzles.length === 0) {
      handleError({ response: { data: { error: 'Select at least one nozzle' } } })
      return
    }

    setIsSubmitting(true)

    try {
      await fillingStationsService.updatePump(stationId, pump.id, {
        fuelFactPumpId: values.fuelFactPumpId.trim(),
        pumpName: values.pumpName.trim(),
        productType: values.productType,
        isActive: values.isActive,
        nozzles: values.nozzles,
      })
      onSuccess()
      onClose()
    } catch (error) {
      handleError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <ModalWrapper showModal={isOpen} closeModal={handleClose} title='Edit Pump' hideHeader={false}>
      <div className='add-pump-modal'>
        <form onSubmit={handleSubmit} className='add-pump-modal__form'>
          <div className='add-pump-modal__field'>
            <label className='add-pump-modal__label'>Fuel Fact Pump ID *</label>
            <input
              type='text'
              name='fuelFactPumpId'
              value={values.fuelFactPumpId}
              onChange={handleChange}
              className='form-control'
              placeholder='Enter pump ID'
              required
            />
          </div>

          <div className='add-pump-modal__field'>
            <label className='add-pump-modal__label'>Pump Name *</label>
            <input
              type='text'
              name='pumpName'
              value={values.pumpName}
              onChange={handleChange}
              className='form-control'
              placeholder='Enter pump name'
              required
            />
          </div>

          <div className='add-pump-modal__field'>
            <label className='add-pump-modal__label'>Product Type *</label>
            <select
              name='productType'
              value={values.productType}
              onChange={handleChange}
              className='form-control'
              required
              disabled={availableProductTypes.length === 0}
            >
              <option value='' disabled>
                Select product
              </option>
              {availableProductTypes.map((productType) => (
                <option key={productType} value={productType}>
                  {productType}
                </option>
              ))}
            </select>
          </div>

          <PumpNozzleSelector
            value={values.nozzles}
            onChange={(nozzles) => setValues((currentValues) => ({ ...currentValues, nozzles }))}
          />

          <div className='add-pump-modal__field'>
            <label className='add-pump-modal__status-option'>
              <input
                type='checkbox'
                name='isActive'
                checked={values.isActive}
                onChange={handleChange}
              />
              <span>
                <strong>Active pump</strong>
                <small>Include this pump in station operations and reports.</small>
              </span>
            </label>
          </div>

          <div className='add-pump-modal__actions'>
            <button
              type='button'
              className='add-pump-modal__cancel-button'
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button type='submit' className='add-pump-modal__submit-button' disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update Pump'}
            </button>
          </div>
        </form>
      </div>
      <ToastContainer />
    </ModalWrapper>
  )
}

export default EditPumpModal
